import { Button, Modal } from "@mantine/core";
import IconAlertTriangle from "@src/assets/icons/tabler/alert-triangle.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { ROUTES } from "@src/common/constants";
import { BuyButton } from "@src/components/plan/buy-button";
import { useActivateStarter } from "@src/components/plan/mutations/activate-starter";
import { useNavigate } from "react-router-dom";

const LOST_PRO_FEATURES = [
	"Add unlimited boards and cards (Starter limit applies)",
	"Image models (Nano Banana, GPT, Stable Diffusion, etc.)",
	"Thinking models (GPT-o3, Grok, Deepseek etc.)",
	"DOCX export & Sent as email",
	"Higher AI credits allowance (Starter has a weekly quota)",
	"Add board editors (Starter limit applies)",
] as const;

interface TrialDowngradeWarningModalProps {
	opened: boolean;
	onClose: () => void;
	onDowngradeSuccess?: () => void;
	variant?: "in-trial" | "trial-ended" | "pro-active";
	proCheckoutPriceId?: string | null;
}

export function TrialDowngradeWarningModal({
	opened,
	onClose,
	onDowngradeSuccess,
	variant = "in-trial",
	proCheckoutPriceId,
}: TrialDowngradeWarningModalProps) {
	const { mutate: activateStarter, isPending } = useActivateStarter();
	const navigate = useNavigate();

	function handleConfirmDowngrade() {
		activateStarter(undefined, {
			onSuccess: () => {
				onClose();
				onDowngradeSuccess?.();
				navigate(`${ROUTES.checkoutReturn}?downgraded=true`);
			},
		});
	}

	const isTrialEnded = variant === "trial-ended";
	const isProActive = variant === "pro-active";

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			centered
			withCloseButton={false}
			zIndex={4000}
			size="420px"
			classNames={{
				content:
					"rounded-2xl border border-border-subtle bg-surface-default shadow-2xl",
			}}
			onClick={(e) => e.stopPropagation()}
		>
			<div className="p-6 space-y-5">
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-center gap-3">
						<div className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-500/15 shrink-0">
							<IconAlertTriangle className="w-5 h-5 text-amber-500" />
						</div>
						<h2 className="text-base font-bold text-text-primary leading-tight">
							You'll lose these Pro features
						</h2>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="p-1 rounded-lg hover:bg-surface-hover-subtle transition-colors shrink-0"
						title="Close"
						aria-label="Close warning"
					>
						<IconX className="w-4 h-4 text-text-muted" />
					</button>
				</div>

				<p className="text-sm text-text-secondary">
					{isTrialEnded
						? "Upgrade to Pro to keep everything, or switch to Starter and lose access to:"
						: "Switching to Starter will immediately remove access to:"}
				</p>

				<ul className="space-y-2">
					{LOST_PRO_FEATURES.map((feature) => (
						<li
							key={feature}
							className="flex items-center gap-2.5 text-sm text-text-primary"
						>
							<span className="flex items-center justify-center w-4 h-4 rounded-full bg-danger-soft shrink-0 text-danger font-bold text-[10px]">
								✕
							</span>
							{feature}
						</li>
					))}
				</ul>

				<div className="flex flex-col gap-2 pt-1">
					{isTrialEnded ? (
						<BuyButton
							priceId={proCheckoutPriceId ?? null}
							variant="cta"
							disabled={!proCheckoutPriceId}
							className="w-full h-10 rounded-xl"
							onCheckoutComplete={onClose}
						>
							Proceed to Pro
						</BuyButton>
					) : (
						<Button
							onClick={onClose}
							variant="cta"
							classNames={{ root: "w-full h-10 rounded-xl" }}
						>
							{isProActive ? "Keep Pro" : "Keep Pro Trial"}
						</Button>
					)}
					<button
						type="button"
						onClick={handleConfirmDowngrade}
						disabled={isPending}
						className="w-full h-10 rounded-xl text-sm font-semibold text-text-secondary hover:text-danger hover:bg-danger-soft transition-colors disabled:opacity-50 disabled:pointer-events-none"
						title="Confirm downgrade to Starter"
					>
						{isPending ? "Downgrading…" : "Downgrade to Starter"}
					</button>
				</div>
			</div>
		</Modal>
	);
}
